import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, Send, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

type Segment = "avvocato" | "studio" | "azienda" | "altro";

export function ContactSection() {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [segment, setSegment] = useState<Segment>("avvocato");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en" : "it";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, organization, segment, message, website, locale }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || t("contact.form.error"));
      setDone(true);
    } catch (e: any) {
      setError(e?.message ?? t("contact.form.error"));
    } finally {
      setLoading(false);
    }
  };

  const whatsappMessage = (() => {
    const segLabel = t(`contact.form.segments.${segment}`);
    const trimmed = message.trim();
    if (locale === "en") {
      return [
        "Hello LexAIPro team, I'd like to learn more about the platform.",
        name.trim() && `• Name: ${name.trim()}`,
        email.trim() && `• Email: ${email.trim()}`,
        organization.trim() && `• Organization: ${organization.trim()}`,
        `• Profile: ${segLabel}`,
        trimmed && `• Request: ${trimmed}`,
      ]
        .filter(Boolean)
        .join("\n");
    }
    return [
      "Ciao team LexAIPro, vorrei saperne di più sulla piattaforma.",
      name.trim() && `• Nome: ${name.trim()}`,
      email.trim() && `• Email: ${email.trim()}`,
      organization.trim() && `• Organizzazione: ${organization.trim()}`,
      `• Profilo: ${segLabel}`,
      trimmed && `• Richiesta: ${trimmed}`,
    ]
      .filter(Boolean)
      .join("\n");
  })();

  const segments: Segment[] = ["avvocato", "studio", "azienda", "altro"];

  return (
    <section id="contatti" className="relative overflow-hidden bg-background py-16 sm:py-24 md:py-28">
      <div className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 11"
          eyebrow={t("contact.eyebrow")}
          title={t("contact.title")}
          subtitle={t("contact.subtitle")}
        />

        <Reveal className="mt-10" y={24} blur={8}>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
            {done ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <CheckCircle2 className="h-12 w-12 text-success" />
                <p className="text-base font-medium">{t("contact.form.success")}</p>
                <button
                  type="button"
                  onClick={() => openWhatsAppContact(whatsappMessage)}
                  className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-accent transition-colors hover:text-accent/80"
                >
                  {t("contact.form.whatsapp")} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-name">{t("contact.form.name")}</Label>
                    <Input
                      id="ct-name"
                      required
                      maxLength={120}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("contact.form.namePlaceholder")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-email">{t("contact.form.email")}</Label>
                    <Input
                      id="ct-email"
                      type="email"
                      required
                      maxLength={255}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("contact.form.emailPlaceholder")}
                    />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-org">{t("contact.form.organization")}</Label>
                    <Input
                      id="ct-org"
                      maxLength={160}
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      placeholder={t("contact.form.organizationPlaceholder")}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ct-segment">{t("contact.form.segment")}</Label>
                    <select
                      id="ct-segment"
                      value={segment}
                      onChange={(e) => setSegment(e.target.value as Segment)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {segments.map((s) => (
                        <option key={s} value={s}>
                          {t(`contact.form.segments.${s}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="ct-message">{t("contact.form.message")}</Label>
                  <Textarea
                    id="ct-message"
                    required
                    maxLength={4000}
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t("contact.form.messagePlaceholder")}
                  />
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                {error && <p className="text-sm text-destructive">{error}</p>}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button type="submit" disabled={loading} className="flex-1">
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("contact.form.submitting")}
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {t("contact.form.submit")}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openWhatsAppContact(whatsappMessage)}
                    className="flex-1 border-[#25D366]/40 text-[#1faa55] hover:bg-[#25D366]/10 hover:text-[#1faa55]"
                  >
                    {t("contact.form.whatsapp")}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground">{t("contact.form.privacy")}</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
